import { Contract, formatUnits, getAddress, parseUnits } from 'ethers';
import {
  WorkLobDAO_abi,
  WorkLobDAO_address,
  LOB_TOKEN_ABI,
  LOB_TOKEN_ADDRESS,
  USDC_TOKEN_ABI,
  USDC_TOKEN_ADDRESS,
} from './Constants';
import { USDC_DECIMALS } from './usdc';
import { withBaseMainnetProvider } from './baseRpc';
import api, { API_ENDPOINTS } from './api';
import { ensureErc20Allowance } from './erc20Allowance';
import { isDaoDisputeOnChainEnabled, DAO_DISPUTE_PAYOUT_DISABLED_MSG } from './daoDisputePayout';

/**
 * Get DAO contract instance
 */
export const getDAOContract = (signerOrProvider) => {
  if (!signerOrProvider) {
    throw new Error('Signer or provider is required');
  }
  if (!WorkLobDAO_address || WorkLobDAO_address === '') {
    throw new Error('DAO contract address not configured. Please set REACT_APP_WORKLOB_DAO_CONTRACT_ADDRESS');
  }
  if (!WorkLobDAO_abi || WorkLobDAO_abi.length === 0) {
    throw new Error('DAO contract ABI not loaded. Please compile the contract and add the ABI to Constants.js');
  }
  return new Contract(WorkLobDAO_address, WorkLobDAO_abi, signerOrProvider);
};

/**
 * Get LOB token contract instance
 */
export const getLOBTokenContract = (signerOrProvider) => {
  if (!signerOrProvider) {
    throw new Error('Signer or provider is required');
  }
  return new Contract(LOB_TOKEN_ADDRESS, LOB_TOKEN_ABI, signerOrProvider);
};

/**
 * USDC pool balance on WorkLobDAO (on-chain usdtPoolBalance / getETHPoolBalance).
 */
export const getUSDCPoolBalance = async (signerOrProvider) => {
  const read = async (provider) => {
    const daoContract = getDAOContract(provider);
    let balanceWei;
    try {
      balanceWei = await daoContract.usdtPoolBalance();
    } catch (_) {
      balanceWei = await daoContract.getETHPoolBalance();
    }
    return formatUnits(balanceWei, USDC_DECIMALS);
  };
  if (signerOrProvider) return read(signerOrProvider);
  return withBaseMainnetProvider(read);
};

/** @deprecated Name kept for compatibility — returns USDC pool balance. */
export const getETHPoolBalance = getUSDCPoolBalance;

/**
 * LOB token pool balance on WorkLobDAO.
 */
export const getLOBTokenPoolBalance = async (signerOrProvider) => {
  const read = async (provider) => {
    const daoContract = getDAOContract(provider);
    let balanceWei;
    try {
      balanceWei = await daoContract.lobTokenPoolBalance();
    } catch (_) {
      balanceWei = await daoContract.getLOBTokenPoolBalance();
    }
    return formatUnits(balanceWei, 18);
  };
  if (signerOrProvider) return read(signerOrProvider);
  return withBaseMainnetProvider(read);
};

/**
 * Fetch DAO pool balances — prefer backend (single server RPC), fallback to browser RPC.
 */
export const fetchDAOPoolBalances = async () => {
  try {
    const { data } = await api.get(API_ENDPOINTS.DEPLOYER.DAO_POOL_BALANCES);
    return {
      usdcPoolBalance: data.usdcPoolBalance ?? data.usdc ?? '0',
      lobPoolBalance: data.lobPoolBalance ?? data.lob ?? '0',
      network: data.network,
      chainId: data.chainId,
      source: 'api',
    };
  } catch (apiError) {
    console.warn('DAO pool API fetch failed, using client RPC:', apiError?.message);
    const usdcPoolBalance = await getUSDCPoolBalance();
    const lobPoolBalance = await getLOBTokenPoolBalance();
    return { usdcPoolBalance, lobPoolBalance, source: 'rpc' };
  }
};

/**
 * Poll on-chain USDC pool after a funding tx (handles RPC lag).
 */
export const waitForUSDCPoolBalance = async ({
  signerOrProvider,
  minAmount = 0,
  maxAttempts = 12,
  delayMs = 1500,
} = {}) => {
  let last = '0';
  for (let i = 0; i < maxAttempts; i++) {
    const { usdcPoolBalance } = await fetchDAOPoolBalances();
    last = usdcPoolBalance;
    if (parseFloat(last) > minAmount) {
      return last;
    }
    await new Promise((r) => setTimeout(r, delayMs));
  }
  return last;
};

/**
 * Get voter reward amount
 */
export const getVoterRewardAmount = async (provider) => {
  try {
    if (!provider) {
      throw new Error('Provider is required');
    }
    const daoContract = getDAOContract(provider);
    console.log('Fetching voter reward amount from contract:', WorkLobDAO_address);
    const amount = await daoContract.getVoterRewardAmount();
    console.log('Raw voter reward amount from contract:', amount.toString());
    const formatted = formatUnits(amount, 18);
    console.log('Formatted voter reward amount:', formatted);
    return formatted;
  } catch (error) {
    console.error('Error getting voter reward amount:', error);
    console.error('Error details:', {
      message: error.message,
      code: error.code,
      data: error.data,
      contractAddress: WorkLobDAO_address,
      hasABI: WorkLobDAO_abi && WorkLobDAO_abi.length > 0
    });
    throw error;
  }
};

/**
 * Fund USDC / payment-token pool (deployer only). Contract method is still `fundETHPool` historically.
 */
export const fundETHPool = async (signer, amount) => {
  try {
    const daoContract = getDAOContract(signer);
    let onChainPayment;
    try {
      onChainPayment = await daoContract.paymentToken();
    } catch (_) {
      onChainPayment = null;
    }
    if (
      onChainPayment &&
      getAddress(onChainPayment) !== getAddress(USDC_TOKEN_ADDRESS)
    ) {
      throw new Error(
        `DAO payment token (${onChainPayment}) does not match configured USDC (${USDC_TOKEN_ADDRESS}). Update REACT_APP_USDC_TOKEN_ADDRESS or redeploy the DAO with the correct token.`
      );
    }

    const token = new Contract(USDC_TOKEN_ADDRESS, USDC_TOKEN_ABI, signer);
    const decimals = Number(await token.decimals());
    const amountWei = parseUnits(amount.toString(), decimals);

    const userAddress = await signer.getAddress();
    const bal = await token.balanceOf(userAddress);
    if (bal < amountWei) {
      throw new Error(
        `Insufficient USDC in wallet. Need ${formatUnits(amountWei, decimals)} USDC, have ${formatUnits(bal, decimals)} USDC.`
      );
    }

    await ensureErc20Allowance(
      signer,
      USDC_TOKEN_ADDRESS,
      USDC_TOKEN_ABI,
      WorkLobDAO_address,
      amountWei
    );

    try {
      await daoContract.fundETHPool.staticCall(amountWei);
    } catch (pre) {
      const data = typeof pre?.data === 'string' ? pre.data : '';
      if (data.startsWith('0xfb8f41b2')) {
        throw new Error(
          'USDC allowance too low for the DAO contract. Approve again or retry in a few seconds (RPC lag after approve).'
        );
      }
      if (data.startsWith('0xe450d38c')) {
        throw new Error(
          'Insufficient USDC balance for this transfer (wallet balance lower than amount).'
        );
      }
      throw pre;
    }

    const tx = await daoContract.fundETHPool(amountWei);
    const receipt = await tx.wait();
    return {
      success: true,
      txHash: receipt.hash,
      blockNumber: receipt.blockNumber
    };
  } catch (error) {
    console.error('Error funding DAO USDC pool:', error);
    throw error;
  }
};

/**
 * Withdraw ETH from pool (deployer only)
 */
export const withdrawETH = async (signer, toAddress, amount) => {
  try {
    const daoContract = getDAOContract(signer);
    const token = new Contract(USDC_TOKEN_ADDRESS, USDC_TOKEN_ABI, signer);
    const decimals = Number(await token.decimals());
    const amountWei = parseUnits(amount.toString(), decimals);
    const tx = await daoContract.withdrawETH(toAddress, amountWei);
    const receipt = await tx.wait();
    return {
      success: true,
      txHash: receipt.hash,
      blockNumber: receipt.blockNumber
    };
  } catch (error) {
    console.error('Error withdrawing from DAO USDC pool:', error);
    throw error;
  }
};

/**
 * Fund LOB token pool (deployer only)
 * Note: Requires token approval first
 */
export const fundLOBTokenPool = async (signer, amount) => {
  try {
    const daoContract = getDAOContract(signer);
    const amountWei = parseUnits(amount.toString(), 18);

    await ensureErc20Allowance(
      signer,
      LOB_TOKEN_ADDRESS,
      LOB_TOKEN_ABI,
      WorkLobDAO_address,
      amountWei
    );

    await daoContract.fundLOBTokenPool.staticCall(amountWei);
    const tx = await daoContract.fundLOBTokenPool(amountWei);
    const receipt = await tx.wait();
    return {
      success: true,
      txHash: receipt.hash,
      blockNumber: receipt.blockNumber
    };
  } catch (error) {
    console.error('Error funding LOB token pool:', error);
    throw error;
  }
};

/**
 * Withdraw LOB tokens from pool (deployer only)
 */
export const withdrawLOBToken = async (signer, toAddress, amount) => {
  try {
    const daoContract = getDAOContract(signer);
    const amountWei = parseUnits(amount.toString(), 18);
    const tx = await daoContract.withdrawLOBToken(toAddress, amountWei);
    const receipt = await tx.wait();
    return {
      success: true,
      txHash: receipt.hash,
      blockNumber: receipt.blockNumber
    };
  } catch (error) {
    console.error('Error withdrawing LOB tokens:', error);
    throw error;
  }
};

/**
 * Set voter reward amount (deployer only)
 */
export const setVoterRewardAmount = async (signer, amount) => {
  try {
    console.log('Setting voter reward amount:', amount);
    const daoContract = getDAOContract(signer);
    const amountWei = parseUnits(amount.toString(), 18);
    console.log('Amount in Wei:', amountWei.toString());
    console.log('Calling setVoterRewardAmount on contract:', WorkLobDAO_address);
    const tx = await daoContract.setVoterRewardAmount(amountWei);
    console.log('Transaction sent, hash:', tx.hash);
    console.log('Waiting for confirmation...');
    const receipt = await tx.wait();
    console.log('Transaction confirmed in block:', receipt.blockNumber);
    console.log('Transaction hash:', receipt.hash);
    
    // Verify the value was set by reading it back
    try {
      const verifyAmount = await daoContract.getVoterRewardAmount();
      console.log('Verified voter reward amount after setting:', formatUnits(verifyAmount, 18));
    } catch (verifyError) {
      console.warn('Could not verify voter reward amount immediately:', verifyError);
    }
    
    return {
      success: true,
      txHash: receipt.hash,
      blockNumber: receipt.blockNumber
    };
  } catch (error) {
    console.error('Error setting voter reward amount:', error);
    console.error('Error details:', {
      message: error.message,
      code: error.code,
      data: error.data,
      reason: error.reason
    });
    throw error;
  }
};

/**
 * Voter withdrawal - withdraw LOB tokens from LOB pool
 */
export const voterWithdrawal = async (signer, amount) => {
  try {
    const daoContract = getDAOContract(signer);
    const amountWei = parseUnits(amount.toString(), 18);
    const tx = await daoContract.voterWithdrawal(amountWei);
    const receipt = await tx.wait();
    return {
      success: true,
      txHash: receipt.hash,
      blockNumber: receipt.blockNumber
    };
  } catch (error) {
    console.error('Error withdrawing voter reward:', error);
    throw error;
  }
};

/**
 * Resolve dispute with backend EIP-712 authorization (secured DAO only).
 * Caller must be the DAO member wallet (`resolver`) — matches connected account in the app.
 */
export const resolveDisputeWithAuth = async (signer, auth, { serverAuthorized = false } = {}) => {
  if (!serverAuthorized && !isDaoDisputeOnChainEnabled()) {
    throw new Error(DAO_DISPUTE_PAYOUT_DISABLED_MSG);
  }
  if (!signer) {
    throw new Error('Signer is required to resolve dispute');
  }
  if (!auth || auth.method !== 'resolveDisputeWithAuth') {
    throw new Error('Missing resolveDisputeWithAuth authorization from server');
  }

  let validSigner = signer;
  if (signer.provider && !signer.sendTransaction) {
    validSigner = await signer.provider.getSigner();
  }

  const connected = (await validSigner.getAddress()).toLowerCase();
  if (connected !== String(auth.resolver).toLowerCase()) {
    throw new Error('Connected wallet must match the authorized DAO member wallet');
  }

  const daoContract = getDAOContract(validSigner);
  if (typeof daoContract.resolveDisputeWithAuth !== 'function') {
    throw new Error(
      'DAO contract does not support resolveDisputeWithAuth. Deploy the secured WorkLobDAO and update REACT_APP_WORKLOB_DAO_CONTRACT_ADDRESS.'
    );
  }

  try {
    const paused = await daoContract.disputeResolutionPaused();
    if (paused) {
      throw new Error(
        'Dispute resolution is paused on the DAO contract. Run smart-contract/scripts/unpause-dao-disputes.js (or setDisputeResolutionPaused(false) as contract owner).'
      );
    }
  } catch (pauseErr) {
    if (pauseErr?.message?.includes('paused on the DAO')) throw pauseErr;
  }

  // Wei strings from backend — ethers v6 coerces these to uint256 (avoids ESLint BigInt global)
  const args = [
    auth.proposalIdBytes32,
    auth.clientWalletAddress,
    auth.talentWalletAddress,
    auth.clientWei,
    auth.talentWei,
    auth.resolver,
    auth.deadline,
    auth.signature,
  ];

  try {
    await daoContract.resolveDisputeWithAuth.staticCall(...args);
  } catch (pre) {
    const msg = pre?.shortMessage || pre?.reason || pre?.message || 'Transaction would revert';
    throw new Error(msg);
  }

  const tx = await daoContract.resolveDisputeWithAuth(...args);
  const receipt = await tx.wait();
  return {
    success: true,
    txHash: receipt.hash,
    blockNumber: receipt.blockNumber,
  };
};

/**
 * @deprecated Use resolveDisputeWithAuth — public resolveDispute is disabled on secured DAO.
 */
export const resolveDispute = async () => {
  throw new Error(
    'resolveDispute is disabled. Deploy the secured DAO and use resolveDisputeWithAuth from the governance flow.'
  );
};

/**
 * Referral withdrawal - withdraw LOB tokens from LOB pool
 */
export const referralWithdrawal = async (signer, amount) => {
  try {
    // Validate signer - ensure it's a signer, not just a provider
    if (!signer) {
      throw new Error('Signer is required to withdraw referral earnings');
    }
    
    // Ensure we have a signer that can send transactions
    let validSigner = signer;
    if (signer.provider && !signer.sendTransaction) {
      // If it's a provider, get the signer from it
      try {
        validSigner = await signer.provider.getSigner();
      } catch (error) {
        throw new Error('Unable to get signer from provider. Please ensure your wallet is connected.');
      }
    }
    
    const daoContract = getDAOContract(validSigner);
    
    // Format amount for parseEther (max 18 decimals)
    const formatForParseEther = (value) => {
      if (value === 0 || value === '0') {
        return '0';
      }
      const numValue = Number(value);
      const formatted = numValue.toFixed(18);
      // Remove trailing zeros
      return formatted.replace(/\.?0+$/, '');
    };
    
    const amountFormatted = formatForParseEther(amount);
    const amountWei = parseUnits(amountFormatted, 18);
    
    console.log('Calling referralWithdrawal with:', {
      amount: amount.toString(),
      amountFormatted,
      amountWei: amountWei.toString(),
      signerType: validSigner.constructor?.name || typeof validSigner
    });
    
    const tx = await daoContract.referralWithdrawal(amountWei);
    const receipt = await tx.wait();
    return {
      success: true,
      txHash: receipt.hash,
      blockNumber: receipt.blockNumber
    };
  } catch (error) {
    console.error('Error withdrawing referral earnings:', error);
    throw error;
  }
};

/**
 * Blog withdrawal - withdraw LOB tokens from LOB pool
 */
export const blogWithdrawal = async (signer, amount) => {
  try {
    // Validate signer - ensure it's a signer, not just a provider
    if (!signer) {
      throw new Error('Signer is required to withdraw blog earnings');
    }
    
    // Ensure we have a signer that can send transactions
    let validSigner = signer;
    if (signer.provider && !signer.sendTransaction) {
      // If it's a provider, get the signer from it
      try {
        validSigner = await signer.provider.getSigner();
      } catch (error) {
        throw new Error('Unable to get signer from provider. Please ensure your wallet is connected.');
      }
    }
    
    const daoContract = getDAOContract(validSigner);
    
    // Format amount for parseEther (max 18 decimals)
    const formatForParseEther = (value) => {
      if (value === 0 || value === '0') {
        return '0';
      }
      const numValue = Number(value);
      const formatted = numValue.toFixed(18);
      // Remove trailing zeros
      return formatted.replace(/\.?0+$/, '');
    };
    
    const amountFormatted = formatForParseEther(amount);
    const amountWei = parseUnits(amountFormatted, 18);
    
    console.log('Calling blogWithdrawal with:', {
      amount: amount.toString(),
      amountFormatted,
      amountWei: amountWei.toString(),
      signerType: validSigner.constructor?.name || typeof validSigner
    });
    
    const tx = await daoContract.blogWithdrawal(amountWei);
    const receipt = await tx.wait();
    return {
      success: true,
      txHash: receipt.hash,
      blockNumber: receipt.blockNumber
    };
  } catch (error) {
    console.error('Error withdrawing blog earnings:', error);
    throw error;
  }
};
